import * as React from "react";

import {
  useCreateAdminBulkContractFamilyPipelineRunMutation,
  useCreateSupplierFolderContractFamilyPipelineRunMutation,
  useGetContractFamilyPipelineConfigurationQuery,
  useStartPipelineCategorisationMutation,
} from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getPipelineErrorMessage } from "@/views/contract-families/pipeline/pipelineErrors";

interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (runId: string) => void;
  contextFolderName?: string;
}

type Props = BaseProps &
  (
    | { supplierContractFolderId: string; adminSelection?: never }
    | {
        supplierContractFolderId?: never;
        adminSelection: {
          contextFolderFseId: string;
          selectedFileFseIds: string[];
          selectedFolderFseIds: string[];
        };
      }
  );

export const ContractFamilySnapshotSetupModal: React.FC<Props> = ({
  open,
  onOpenChange,
  supplierContractFolderId,
  adminSelection,
  onCreated,
  contextFolderName,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [useBatchApi, setUseBatchApi] = React.useState(false);
  const configuration = useGetContractFamilyPipelineConfigurationQuery(undefined, { skip: !open });
  const [createRun, { isLoading }] = useCreateSupplierFolderContractFamilyPipelineRunMutation();
  const [createAdminRun, { isLoading: isAdminLoading }] = useCreateAdminBulkContractFamilyPipelineRunMutation();
  const [startCategorisation, { isLoading: isStartingCategorisation }] = useStartPipelineCategorisationMutation();
  const isConfigured = Boolean(
    configuration.data?.fixed_template_id &&
    configuration.data.default_family_collection_id &&
    configuration.data.publisher_user_id,
  );

  React.useEffect(() => {
    if (open) {
      setError(null);
      setUseBatchApi(false);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      const execution_mode = useBatchApi ? "batch" : "sync";
      const run = supplierContractFolderId
        ? await createRun({
            supplier_contract_folder_id: supplierContractFolderId,
            execution_mode,
          }).unwrap()
        : await createAdminRun({
            context_folder_fse_id: adminSelection!.contextFolderFseId,
            selected_file_fse_ids: adminSelection!.selectedFileFseIds,
            selected_folder_fse_ids: adminSelection!.selectedFolderFseIds,
            execution_mode,
          }).unwrap();
      await startCategorisation(run.id).unwrap();
      onCreated(run.id);
    } catch (requestError) {
      setError(getPipelineErrorMessage(requestError));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Contract Summary</DialogTitle>
          <DialogDescription>
            Categorise every supported document in this Supplier folder, review the proposed families, then create their
            summaries.
          </DialogDescription>
        </DialogHeader>

        {configuration.isError ? (
          <div role="alert" className="space-y-2 text-sm text-red-700">
            <p>{getPipelineErrorMessage(configuration.error)}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void configuration.refetch()}>
              Retry configuration
            </Button>
          </div>
        ) : null}

        {configuration.data ? (
          <dl className="grid gap-3 rounded-md border bg-slate-50 p-4 text-sm">
            <div>
              <dt className="font-medium text-slate-700">Supplier contract folder</dt>
              <dd className="mt-1 text-slate-500">{contextFolderName ?? "Current folder"}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">Snapshot Template</dt>
              <dd className="mt-1 text-slate-500">
                {configuration.data.fixed_template_name ??
                  (configuration.data.fixed_template_id ? "Configured template" : "Not configured")}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">Master Collection</dt>
              <dd className="mt-1 text-slate-500">
                {configuration.data.default_family_collection_name ??
                  (configuration.data.default_family_collection_id ? "Configured collection" : "Not configured")}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">Publisher</dt>
              <dd className="mt-1 text-slate-500">
                {configuration.data.publisher_user_name ??
                  (configuration.data.publisher_user_id ? "Configured owner" : "Not configured")}
              </dd>
            </div>
          </dl>
        ) : null}

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={useBatchApi}
            onChange={event => setUseBatchApi(event.target.checked)}
          />
          <span>
            <span className="font-medium">Use Batch API</span>
            <span className="block text-slate-500">
              Runs asynchronously at lower provider cost and can take longer.
            </span>
          </span>
        </label>

        {!configuration.isFetching && configuration.data && !isConfigured ? (
          <p role="alert" className="text-sm text-amber-700">
            Ask an organization administrator to configure the Snapshot Template, Master Collection, and Publisher.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            onClick={() => void submit()}
            disabled={!isConfigured || isLoading || isAdminLoading || isStartingCategorisation}
          >
            {isLoading || isAdminLoading || isStartingCategorisation ? "Creating…" : "Start categorisation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
